import React, { useEffect } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { CssBaseline, useMediaQuery, Container, Typography } from '@material-ui/core';
import { MuiThemeProvider, createTheme, responsiveFontSizes } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import LoginForm from './components/Auth/Login/LoginForm';
import LoginWithToken from './components/Auth/common/LoginWithToken';
import RegisterForm from './components/Auth/Register/RegisterForm';
import Logout from './components/logout/Logout';
import Layout from './components/Layout/Layout';
import { setLandscape, fetchGlobalSettings } from './store';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import theme from '@amcharts/amcharts4/.internal/themes/animated';
import { getColorPalette } from './palette';
import { createCustomTheme } from './theme';
import { grey } from '@material-ui/core/colors';
import * as actions from './store/index';
import { useTranslation } from 'react-i18next';
import IFUPdf from './components/IFU/IFUPdf';
import ResultView from './components/Layout/ResultView/ResultView';
import Settings from './components/Layout/Dialogs/Settings/Settings';
import animation from './resources/processing_animation.gif';
import { fetchGet } from './helpers/fetching';
import { setLanguage } from './store/slices/global_settings';

const App = () => {
  const { i18n, t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { client, client_id, isReady, language } = useSelector((store) => store.global_settings);
  const darkState = useSelector((store) => store.gui.darkState);
  const landscape = useMediaQuery('(min-aspect-ratio: 1/1)');
  const smallScreen = useMediaQuery('(max-width: 800px)');
  const username = localStorage.getItem('username');
  const softwareVersion = process.env.REACT_APP_SOFTWARE_VERSION || '2.0.0';

  const isAuthPage =
    history.location.pathname.includes('/login') || history.location.pathname.includes('/register');

  const [
    mainPrimaryColor,
    mainPrimaryColorDark,
    mainSecondaryColor,
    mainSecondaryColorDark,
    mainBackgroundColor,
    mainPaperColor,
    accentColor,
  ] = getColorPalette();

  let myTheme = createCustomTheme(
    mainBackgroundColor,
    mainPaperColor,
    accentColor,
    mainPrimaryColor,
    mainPrimaryColorDark,
    mainSecondaryColor,
    mainSecondaryColorDark,
  );

  myTheme.overrides = {
    MuiCssBaseline: {
      '@global': {
        '*::-webkit-scrollbar': {
          width: '0.4em',
          scrollBehavior: 'smooth',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: grey[600],
          borderRadius: '5px',
        },
      },
    },

    MuiButton: {
      root: {
        fontSize: myTheme.fonts.responsiveMedium,
        borderRadius: '5px',
      },
      containedSizeSmall: {
        fontSize: myTheme.fonts.responsiveMedium,
        borderRadius: '5px',
      },
      outlinedSizeSmall: {
        fontSize: myTheme.fonts.responsiveMedium,
        borderRadius: '5px',
      },
      iconSizeMedium: {
        '& > *:first-child': {
          fontSize: myTheme.fonts.responsiveBig,
        },
      },
    },
  };

  if (smallScreen) myTheme.drawerWidth -= 50;

  const fetchUserLanguage = async () => {
    const response = await fetchGet(`user/${username}`);
    if (response.success) {
      dispatch(setLanguage(response.data.language));
    }
  };

  useEffect(() => {
    dispatch(fetchGlobalSettings());
    //  ResizeObserver error boundary fix
    window.addEventListener('error', (e) => {
      console.log('error', e);
      if (
        e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.type === 'error'
      ) {
        e.stopImmediatePropagation();
      }
    });
  }, []);

  useEffect(() => {
    if (!username) return;
    fetchUserLanguage();
  }, [username]);

  useEffect(() => {
    if (language) i18n.changeLanguage(language);
    else if (localStorage.getItem('language')) i18n.changeLanguage(localStorage.getItem('language'));
    else i18n.changeLanguage('en');
  }, [language]);

  useEffect(() => {
    dispatch(setLandscape(landscape));
  }, [landscape, dispatch]);

  useEffect(() => {
    localStorage.setItem('accountName', client);
    localStorage.setItem('accountID', client_id);
    localStorage.setItem('softwareVersion', softwareVersion);
  }, [history, dispatch, client, client_id, softwareVersion]);

  myTheme = responsiveFontSizes(myTheme);

  return (
    <MuiThemeProvider theme={myTheme}>
      <CssBaseline />
      <ErrorBoundary t={t}>
        {!isReady && !isAuthPage && (
          <div
            style={{
              width: '100%',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              opacity: 0.7,
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 2000,
              backgroundColor: '#0d0d0dc4',
            }}>
            <img
              style={{ width: '150px', height: '150px', borderRadius: '50%' }}
              src={animation}
              alt='logoanimation'
            />
            <Typography>{t('api_messages.busy_or_unavailable')}</Typography>
          </div>
        )}
        <Switch>
          <Route path={`/bbox/ifu_:lang`}>{username ? <IFUPdf lang={i18n.language} /> : <Logout />}</Route>

          <Route path='/bbox' render={(props) => <Layout {...props} />} />

          <Route path='/study/:username/:password/:study_id'>
            <ResultView />
          </Route>

          <Route path='/study/:token/:study_id'>
            <ResultView />
          </Route>

          <Route path='/register'>
            <RegisterForm />
          </Route>

          <Route path='/login/:token'>
            <LoginWithToken />
          </Route>

          <Route path='/login' render={(props) => <LoginForm {...props} />} />

          <Route path='/logout'>
            <Logout />
          </Route>

          <Redirect to='/login' />
        </Switch>
      </ErrorBoundary>
    </MuiThemeProvider>
  );
};

export default App;
